import { List, Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';
import { trimTextToEmpty } from 'flow/utils/message-utils';
import {
    createFuseInstance,
    searchFuseInstance,
} from 'flow/utils/search-utils';
import { extractName } from 'flow/utils';
import { selectUserCountryCode } from 'flow/selectors/ExtensionSelectors';
import { createImmutableSelector } from 'flow/selectors/selector-utils';

const getFilterText = (_state, extensionFilter) =>
    trimTextToEmpty(extensionFilter);
const getExtensions = (state) => state.extensions.get('extensions', Map());
const getExternalContacts = (state) =>
    state.contacts.get('externalContacts', Map());
const getThirdPartyContacts = (state) =>
    state.contacts.get('socialIntegrationContacts', Map());

const FIELD_MAPPING = {
    firstname: 'name',
    lastname: 'name',
    anumber: 'fixedNumber',
    mobile: 'mobileNumber',
    alttelephone1: 'altTelephone1',
    alttelephone2: 'altTelephone2',
    address: 'address',
    postalcode: 'postalcode',
    country: 'country',
    city: 'city',
    email: 'email',
    description: 'description',
    department: 'department',
    occupation: 'occupation',
    keywords: 'keywords',
    companyname: 'companyName',
};

const FIELD_WEIGHTS = {
    name: 1.2,
    fixedNumber: 1.1,
    mobileNumber: 1.1,
    altTelephone1: 1.1,
    altTelephone2: 1.1,
    keywords: 1.0,
    companyName: 0.9,
    email: 0.8,
    occupation: 0.7,
    address: 0.6,
    country: 0.5,
    postalcode: 0.4,
    city: 0.3,
    description: 0.2,
    department: 0.1,
};

const DEFAULT_WEIGHT = 1.0;

const getKeys = createSelector(
    (state) => state.customer.get('contactFields', List()),
    (contactFields) =>
        contactFields
            .map((field) => ({
                name: FIELD_MAPPING[field.get('name')] || field.get('name'),
                weight:
                    FIELD_WEIGHTS[
                        FIELD_MAPPING[field.get('name')] || field.get('name')
                    ] || DEFAULT_WEIGHT,
            }))
            .toArray(),
);

const getMergedContactsList = createSelector(
    [getExtensions, getExternalContacts, getThirdPartyContacts],
    (extensions, externalContacts, thirdPartyContacts) => {
        return extensions.merge(externalContacts).merge(thirdPartyContacts);
    },
);

/* Opening a person's page writes that person again, often with equal data.
 * An entity that is the same, or equal, keeps its searchable object, so the
 * search does not build thousands of them again or run again. Each pass keeps
 * only the contacts it saw, so removed contacts, and a previous user's, go at
 * the next search. Until then this cache holds them. */
let searchableByKey = {};

const searchableFor = (
    entity,
    key,
    countryCodeForCustomer,
    previousByKey,
    nextByKey,
) => {
    const cached = previousByKey[key];
    let searchable;
    if (
        cached?.entity === entity &&
        cached.countryCodeForCustomer === countryCodeForCustomer
    ) {
        searchable = cached.searchable;
    } else {
        const built = buildSearchable(entity, key, countryCodeForCustomer);
        searchable =
            cached && isEqual(cached.searchable, built)
                ? cached.searchable
                : built;
    }
    nextByKey[key] = { entity, countryCodeForCustomer, searchable };
    return searchable;
};

const buildSearchable = (entity, key, countryCodeForCustomer) => {
    if (!entity?.get('key')) {
        return null;
    }
    if (
        entity?.get('key').startsWith('contact') ||
        entity?.get('key').startsWith('thirdpartycontact')
    ) {
        return contactToSearchable(entity, key, countryCodeForCustomer);
    }

    if (entity?.get('key').startsWith('extension') && entity?.has('contact')) {
        return extensionToSearchable(entity, countryCodeForCustomer);
    }

    return null;
};

const getNumberFormatByCountryCode = (customerCountryCode, entity, type) => {
    return customerCountryCode === entity.getIn([type, 'countryCode'])
        ? entity.getIn([type, 'nationalFormat'])
        : entity.getIn([type, 'number']);
};

const contactToSearchable = (contact, key, customerCountryCode) => {
    const additionalFields = contact.get('additionalFields', Map()).toJS();

    return {
        key: key || contact.get('key'),
        name: extractName(contact),
        fixedNumber: getNumberFormatByCountryCode(
            customerCountryCode,
            contact,
            'fixed',
        ),
        mobileNumber: getNumberFormatByCountryCode(
            customerCountryCode,
            contact,
            'mobile',
        ),
        altTelephone1: contact.getIn(['altTelephone1', 'number']),
        altTelephone2: contact.getIn(['altTelephone2', 'number']),
        keywords: contact.get('keywords'),
        address: contact.get('address'),
        postalcode: contact.get('postalcode'),
        country: contact.get('country'),
        city: contact.get('city'),
        email: contact.get('email'),
        description: contact.get('description'),
        department: contact.get('department'),
        occupation: contact.get('occupation'),
        companyName: contact.get('companyName'),
        ...additionalFields,
    };
};

const extensionToSearchable = (extension, customerCountryCode) => {
    const additionalFields = extension
        .getIn(['contact', 'additionalFields'], Map())
        .toJS();

    return {
        key: extension.get('key'),
        name: extractName(extension.get('contact')),
        fixedNumber: getNumberFormatByCountryCode(
            customerCountryCode,
            extension.get('contact'),
            'fixed',
        ),
        mobileNumber: getNumberFormatByCountryCode(
            customerCountryCode,
            extension.get('contact'),
            'mobile',
        ),
        altTelephone1: extension.getIn(['contact', 'altTelephone1', 'number']),
        altTelephone2: extension.getIn(['contact', 'altTelephone2', 'number']),
        keywords: extension.getIn(['contact', 'keywords']),
        address: extension.getIn(['contact', 'address']),
        postalcode: extension.getIn(['contact', 'postalcode']),
        country: extension.getIn(['contact', 'country']),
        city: extension.getIn(['contact', 'city']),
        email: extension.getIn(['contact', 'email']),
        description: extension.getIn(['contact', 'description']),
        department: extension.getIn(['contact', 'department']),
        occupation: extension.getIn(['contact', 'occupation']),
        companyName: extension.getIn(['contact', 'companyName']),
        ...additionalFields,
    };
};

const selectSearchables = createSelector(
    [getMergedContactsList, selectUserCountryCode],
    (contacts, countryCode) => {
        const nextByKey = {};
        const searchables = contacts
            .map((entity, key) =>
                searchableFor(
                    entity,
                    key,
                    countryCode,
                    searchableByKey,
                    nextByKey,
                ),
            )
            .valueSeq()
            .filter(Boolean)
            .toList();
        searchableByKey = nextByKey;
        return searchables;
    },
);

const FUZZY_OPTIONS = {
    includeScore: true,
    includeMatches: true,
    shouldSort: true,
    useExtendedSearch: true,
};

// While every searchable is the same object, the index and results stay. A
// searchable is a plain object, so Immutable's is compares it by reference.
const selectFuseInstance = createImmutableSelector(
    [selectSearchables, getKeys],
    (searchables, keys) =>
        createFuseInstance(
            searchables,
            (searchable) => searchable,
            FUZZY_OPTIONS,
            keys,
        ),
);

const selectFuzzySearchResults = createSelector(
    [selectFuseInstance, getFilterText],
    (fuseInstance, filterText) =>
        List(searchFuseInstance(fuseInstance, filterText)),
);

// Without search text, nothing builds the index.
export const fuzzySearchColleaguesList = (state, extensionFilter) =>
    getFilterText(state, extensionFilter)
        ? selectFuzzySearchResults(state, extensionFilter)
        : List();
